import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { useStore } from '@src/models/RootStore';
import * as testService from '@src/services/tests';

const RatingModal = React.lazy(() => import('@src/features/candidates/features/applications/atoms/RatingModal'));

import {
  PageContainer,
  Card,
  // Content Components
  ContentBody,
  // Active Test Components
  FocusContainer,
  TopBar,
  TimerBadge,
  ProgressBarContainer,
  ProgressFill,
  QuestionDisplay,
  AnswerSection,
  StyledTextArea,
  // Common
  PrimaryButton,
  SecondaryButton,
  FadeIn,
  WarningModalOverlay,
  WarningCard,
  CompanyLogoBig,
  DetailText,
  DetailTitle,
  ExampleCard,
  ExampleSection,
  MainContent,
  ScrollableArea,
  Sidebar,
  SidebarContent,
  SplitCard,
  StatLabel,
  StatValue,
  StatGrid,
  StatItem,
  StickyFooter,
  StatContent,
  SampleFadeIn,
  QcmOption,
  ExampleQcmOption,
  Grid2,
  CustomTimerBadge,
  SpinWrapper,
  SubmitButton,
} from './TestDetail.styled';
import { Flex } from 'src/kit';
import { RadioGroupContainer } from '@src/features/tests/atoms/RadioGroup.styled';
import { RadioGroup } from '@src/features/tests/molecules/RadioGroup';

type TestState = 'intro' | 'active' | 'completed';

const formatSeconds = (seconds: number): string => {
  if (seconds < 0) return '0:00';

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '--:--:--';

  const date = new Date(dateString);

  if (isNaN(date.getTime())) return '--:--:--';

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};

const isValidDateString = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

export const TestDetail: React.FC = observer(() => {
  const { testId } = useParams<{ testId: string }>();
  const { testStore, userStore } = useStore();

  const [testState, setTestState] = useState<TestState>('intro');
  const [userAnswer, setUserAnswer] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [finishedTime, setFinishedTime] = useState<string | null>(null);
  const deadlineRef = useRef<number>(0);
  const finishTriggeredRef = useRef(false);
  const submitInProgressRef = useRef(false);
  const submitPausedAtRef = useRef(0);
  const [isFinished, setIsFinished] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [feedbackToDisplay, setFeedbackToDisplay] = useState<any>(null);

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  useEffect(() => {
    if (testId) {
      testStore.loadTestDetails(parseInt(testId, 10));
    }
    if (testStore.finishDate && isValidDateString(testStore.finishDate)) {
      setFinishedTime(testStore.finishDate);
    } else {
      setFinishedTime(null);
    }
  }, [testId, testStore]);

  useEffect(() => {
    if (testState !== 'active' || !testStore.testStarted || testStore.testCompleted) return;

    // The absolute deadline was established in handleStartTest from the /start
    // response (time_left). We deliberately do NOT re-seed from the store here:
    // the shared store can briefly hold a stale time_left, and re-seeding from it
    // (or keying the effect off it) would show the wrong remaining time when a
    // test is resumed after time was spent on the description page.

    const syncTimer = () => {
      if (testState !== 'active' || testStore.testCompleted) return;

      // Freeze the countdown while an answer file is being uploaded so the
      // timer cannot expire mid-submission. handleSubmitFile repays the
      // paused time to deadlineRef once the upload finishes.
      if (submitInProgressRef.current) return;

      const remaining = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
      setTimeRemaining(remaining);

      if (remaining <= 0 && !finishTriggeredRef.current) {
        finishTriggeredRef.current = true;
        testStore.finishTest();
        setTestState('intro');
      }
    };

    // Re-anchor the countdown to the server's authoritative time_left every
    // minute (and on tab focus/visibility) so a backgrounded/frozen tab or a
    // client clock jump cannot leave the display drifted. We call
    // getTestDetails directly (never loadTestDetails) so the shared store is
    // not mutated. Skipped while a file upload is paused so the deadline does
    // not shift underneath the submit-freeze.
    const syncFromServer = async () => {
      if (testState !== 'active' || testStore.testCompleted || submitInProgressRef.current) return;
      try {
        const details = await testService.getTestDetails(parseInt(testId, 10));
        const seconds = Math.max(0, Math.round(details.time_left || 0));
        if (seconds <= 0) {
          if (!finishTriggeredRef.current) {
            finishTriggeredRef.current = true;
            testStore.finishTest();
            setTestState('intro');
          }
          return;
        }
        deadlineRef.current = Date.now() + seconds * 1000;
      } catch (e) {
        // Transient network error: ignore, the next tick/poll retries.
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncTimer();
        syncFromServer();
      }
    };

    syncTimer();
    const interval = setInterval(syncTimer, 1000);
    const poll = setInterval(syncFromServer, 60000);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    return () => {
      clearInterval(interval);
      clearInterval(poll);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
      finishTriggeredRef.current = false;
    };
  }, [testState, testStore.testStarted, testStore.testCompleted, testStore]);

  useEffect(() => {
    if (testStore.currentQuestion) {
      setUserAnswer('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [testStore.currentQuestion?.id]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isNonFdcActive = testState === 'active' && testStore.testType !== 'fdc_test';

      if (isNonFdcActive && !testStore.testCompleted) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return ''; // Required for some other browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [testState, testStore.testType, testStore.testCompleted]);

  useEffect(() => {
    const checkFeedback = async () => {
      if (
        testStore.testCompleted ||
        testStore.state === 'finished' ||
        testStore.state === 'pending' ||
        testStore.state === 'review' ||
        testStore.state === 'missed'
      ) {
        setTestState('completed');
        setIsFinished(true);

        if (testStore.applicationId) {
          await userStore.getApplications();
          const app = userStore.applications.entities.find(a => Number(a.id) === Number(testStore.applicationId));

          if (app && app.feedbacks && app.feedbacks.length > 0) {
            setFeedbackToDisplay(app.feedbacks[0]);
          }
        }
      }
    };

    checkFeedback();
  }, [testStore.testCompleted, testState, testStore.state, testStore.applicationId]);

  const handleStartTest = async () => {
    await testStore.startTest();

    // If the start call failed (network/backend error), the store holds the error
    // and testStarted stays false. Stay on the intro so the candidate can retry
    // instead of entering a dead "active" screen with a frozen timer.
    if (!testStore.testStarted) {
      return;
    }

    // Seed the countdown deadline from the fresh start response. Reading
    // testStore.timeLeft AFTER awaiting startTest guarantees we use the value
    // returned by /start (which already accounts for time spent on the
    // description page), never a stale value left in the shared store.
    // A non-positive time_left means the deadline already passed server-side;
    // seed the deadline to now so the first tick finishes the test instead of
    // freezing at 0:00.
    const seconds = testStore.timeLeft;
    deadlineRef.current = Date.now() + (seconds > 0 ? seconds : 0) * 1000;
    finishTriggeredRef.current = false;

    setTestState('active');
    setShowWarning(false);
  };

  const handleSubmit = async (answer: string) => {
    if (!answer.trim() || testStore.isLoading) return;
    await testStore.submitAnswer(answer);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSubmit(userAnswer);
    }
  };

  const handleAnswer = (answerId: string) => {
    if (!answerId) return;
    setIsLoadingSubmit(true);
    handleSubmit(answerId);
    setIsLoadingSubmit(false);
    setSelectedOption(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setUploadedFile(newFiles[0]);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setUploadedFile(files[0]);
    }
  };

  const handleSubmitFile = async () => {
    if (!uploadedFile) {
      return;
    }

    submitInProgressRef.current = true;
    submitPausedAtRef.current = Date.now();
    try {
      const response = await testStore.submitFiles([uploadedFile]);
      console.log('Submission successful:', response);

      if (response.status === 'processing') {
        setTestState('completed');
        setIsFinished(true);
      }
    } catch (error) {
      console.error('Failed to submit files:', error);
    } finally {
      // Give the paused time back to the countdown so the submission
      // is not penalized by the timer.
      deadlineRef.current += Date.now() - submitPausedAtRef.current;
      submitInProgressRef.current = false;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const renderIntro = () => (
    <FadeIn>
      <SplitCard>
        <Sidebar color={testStore.company?.color} cover={testStore.company?.cover_url}>
          <SidebarContent>
            {testStore.company?.logo_url && <CompanyLogoBig src={testStore.company.logo_url} alt="Logo" />}
            <div>
              <h1>{testStore.company?.name || 'Company Name'}</h1>
              <div className="job-pill">{testStore.job?.position_title || 'Position'}</div>
            </div>
          </SidebarContent>
        </Sidebar>

        <MainContent>
          <ScrollableArea>
            <DetailTitle>{testStore.title}</DetailTitle>

            {testStore.description && testStore.testType != 'fdc_test' && (
              <DetailText dangerouslySetInnerHTML={{ __html: testStore.description }} />
            )}
            {testStore.testType === 'fdc_test' && (
              <DetailText>
                <p>
                  <strong>Important:</strong> Once you click "Start Assessment", the timer will begin immediately. You
                  will have exactly {testStore.timeLimit} minutes to complete the exercise and submit/upload your
                  one-pager within this time frame.
                </p>

                <p>Dear candidate,</p>

                <p>
                  The recruitment team warmly thanks you for participating in this stage of our selection process. We
                  truly appreciate the time and energy you are dedicating to this opportunity.
                </p>

                <p>
                  Clicking the button below will take you to the working interface, where you will find the detailed
                  instructions needed to complete the exercise under the required conditions.
                </p>

                <p>
                  <strong>Please ensure you have:</strong>
                </p>
                <ul>
                  <li>{testStore.timeLimit} minutes of uninterrupted time available</li>
                  <li>Everything ready before you click "Start Assessment"</li>
                  <li>
                    After the {testStore.timeLimit} minutes have passed, you will no longer be able to submit your work.
                  </li>
                </ul>

                <p>We wish you the best of luck!</p>
              </DetailText>
            )}
            <StatGrid>
              <StatItem>
                <div className="icon">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <StatContent>
                  <StatLabel>Time Limit</StatLabel>
                  <StatValue>{testStore.timeLimit} Minutes</StatValue>
                </StatContent>
              </StatItem>
              <StatItem>
                <div className="icon">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <StatContent>
                  <StatLabel>Questions</StatLabel>
                  <StatValue>{testStore.progress?.total || '-'}</StatValue>
                </StatContent>
              </StatItem>
            </StatGrid>

            {testStore.exampleQuestions.length > 0 && (
              <ExampleSection>
                <h3>Example Questions</h3>
                {testStore.exampleQuestions.map((ex, i) => (
                  <ExampleCard key={ex.id || i}>
                    <div>
                      <span className="q-num">Q{i + 1}.</span>
                      <div className="q-text" dangerouslySetInnerHTML={{ __html: ex.text }} />
                    </div>
                    {ex.answers && ex.answers.length > 0 && (
                      <Grid2 columnGap={12.5} marginTop={25} justifyContent="center">
                        {ex.answers.map((ans) => (
                          <ExampleQcmOption
                            key={ans.id}
                            className="opt"
                            dangerouslySetInnerHTML={{ __html: ans.text }}
                          />
                        ))}
                      </Grid2>
                    )}
                  </ExampleCard>
                ))}
              </ExampleSection>
            )}

            <div style={{ height: '20px' }} />
          </ScrollableArea>

          <StickyFooter>
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
              {testStore.state === 'new' ? 'Ready to begin?' : 'Continue where you left off'}
            </div>
            <PrimaryButton onClick={handleStartTest} disabled={testStore.isLoading}>
              {testStore.state === 'new' ? 'Start Assessment' : 'Resume Assessment'}
            </PrimaryButton>
          </StickyFooter>
        </MainContent>
      </SplitCard>
    </FadeIn>
  );

  const renderActive = () => (
    <FocusContainer>
      <TopBar>
        <div className="brand">{testStore.title}</div>
        <TimerBadge isUrgent={timeRemaining < 60}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          {formatSeconds(timeRemaining)}
        </TimerBadge>
      </TopBar>

      <ProgressBarContainer>
        <ProgressFill percentage={testStore.progress?.percentage || 0} />
      </ProgressBarContainer>

      <ContentBody>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#888', marginBottom: '1rem' }}>
            <span>
              Question {testStore.progress?.answered! + 1} of {testStore.progress?.total}
            </span>
            <span>{testStore.progress?.percentage.toFixed(0)}%</span>
          </div>

          <SampleFadeIn key={testStore.currentQuestion?.id}>
            {testStore.currentQuestion && (
              <>
                <QuestionDisplay dangerouslySetInnerHTML={{ __html: testStore.currentQuestion.text }} />

                {testStore.currentQuestion.answers && testStore.currentQuestion.answers.length > 0 ? (
                  <>
                    <RadioGroup
                      options={testStore.currentQuestion.answers!.map((ans) => ({ id: ans.id, text: ans.text }))}
                      onChange={(value) => setSelectedOption(value)}
                    />
                    <SubmitButton onClick={() => handleAnswer(selectedOption!)} disabled={isLoadingSubmit}>
                      {isLoadingSubmit ? 'Submitting...' : 'Submit'}
                    </SubmitButton>
                  </>
                ) : (
                  <AnswerSection>
                    <StyledTextArea
                      ref={inputRef}
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      onKeyDown={handleKeyPress}
                      placeholder="Type your answer here..."
                      disabled={testStore.isLoading}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '1rem',
                      }}
                    >
                      <span style={{ fontSize: '0.8rem', color: '#999' }}>
                        <strong>Ctrl + Enter</strong> to submit
                      </span>
                      <PrimaryButton onClick={handleSubmit} disabled={!userAnswer.trim() || testStore.isLoading}>
                        {testStore.isLoading ? 'Sending...' : 'Submit Answer'}
                      </PrimaryButton>
                    </div>
                  </AnswerSection>
                )}
              </>
            )}
          </SampleFadeIn>
        </div>
      </ContentBody>
    </FocusContainer>
  );

  const renderCustomTest = () => {
    const handleDownload = (fileUrl: string) => {
      if (fileUrl) {
        window.open(fileUrl, '_blank');
      }
    };

    const handleDownloadUploadedFile = (file: File) => {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    return (
      <FadeIn>
        <SplitCard>
          <Sidebar color={testStore.company?.color} cover={testStore.company?.cover_url}>
            <SidebarContent>
              {testStore.company?.logo_url && <CompanyLogoBig src={testStore.company.logo_url} alt="Logo" />}
              <div>
                <h1>{testStore.company?.name || 'Company Name'}</h1>
                <div className="job-pill">{testStore.job?.position_title || 'Position'}</div>
              </div>
            </SidebarContent>
          </Sidebar>

          <MainContent>
            <CustomTimerBadge $isUrgent={timeRemaining < 60}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{formatSeconds(timeRemaining)}</span>
            </CustomTimerBadge>
            <ScrollableArea>
              <DetailTitle>{testStore.title}</DetailTitle>

              {testStore.description && <DetailText dangerouslySetInnerHTML={{ __html: testStore.description }} />}

              <div style={{ marginTop: '2rem' }}>
                <h3
                  style={{
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    color: '#94a3b8',
                    letterSpacing: '0.05em',
                    marginBottom: '1rem',
                  }}
                >
                  Test Documents
                </h3>

                {testStore.customTestDocs.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {testStore.customTestDocs.map((doc) => (
                      <ExampleCard
                        key={doc.id}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '0.25rem' }}>{doc.name}</div>
                          <div style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'capitalize' }}>
                            {doc.file_type}
                          </div>
                        </div>
                        <PrimaryButton
                          onClick={() => handleDownload(doc.file_url)}
                          style={{ width: 'auto', padding: '10px 20px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              />
                            </svg>
                            Download
                          </div>
                        </PrimaryButton>
                      </ExampleCard>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#64748b',
                      background: '#f8fafc',
                      borderRadius: '12px',
                    }}
                  >
                    No documents available for this test.
                  </div>
                )}
              </div>

              <div style={{ height: '20px' }} />
            </ScrollableArea>

            <StickyFooter>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', marginBottom: '1.5rem' }}
              >
                <h3
                  style={{
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    color: '#94a3b8',
                    letterSpacing: '0.05em',
                    margin: 0,
                  }}
                >
                  Submit Your File
                </h3>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                {!uploadedFile ? (
                  <button
                    onClick={handleUploadClick}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '14px 24px',
                      background: isDragging ? '#f1f5f9' : '#f8fafc',
                      border: isDragging ? '2px dashed #94a3b8' : '2px dashed #cbd5e1',
                      borderRadius: '12px',
                      color: '#475569',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      width: '100%',
                    }}
                    onMouseEnter={(e) => {
                      if (!isDragging) {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.borderColor = '#94a3b8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDragging) {
                        e.currentTarget.style.background = '#f8fafc';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                  >
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    {isDragging ? 'Drop File Here' : 'Choose File or Drag & Drop'}
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        columnGap: '1rem',
                        padding: '10px 14px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => handleDownloadUploadedFile(uploadedFile)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f8fafc';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#64748b">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              color: '#1e293b',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {uploadedFile.name}
                          </div>
                          <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                            {formatFileSize(uploadedFile.size)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile();
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          transition: 'background 0.2s ease',
                          zIndex: 1,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#fee2e2';
                          e.stopPropagation();
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'none';
                          e.stopPropagation();
                        }}
                      >
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <PrimaryButton onClick={handleSubmitFile} disabled={testStore.isLoading} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        {testStore.isLoading ? (
                          <>
                            <SpinWrapper>
                              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                              </svg>
                            </SpinWrapper>
                            Analyzing ...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Submit File
                          </>
                        )}
                      </div>
                    </PrimaryButton>
                  </div>
                )}
              </div>
            </StickyFooter>
          </MainContent>
        </SplitCard>
      </FadeIn>
    );
  };

  const renderCompleted = () => (
    <FadeIn>
      <Card style={{ textAlign: 'center', padding: '4rem 2rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
        <h2>Assessment Completed</h2>
        <p style={{ color: '#666', maxWidth: '400px', margin: '0 auto 2rem auto' }}>
          Thank you for completing the <strong>{testStore.title}</strong> for <strong>{testStore.company?.name}</strong>
          .
        </p>

        <div
          style={{
            background: '#f0fdf4',
            padding: '1.5rem',
            borderRadius: '12px',
            color: '#166534',
            marginBottom: '2rem',
          }}
        >
          {testStore.testType === 'fdc_test' ? (
            <>
              Your files have been submitted and are being analyzed. The hiring team will review your results and be in
              touch with the next steps.
            </>
          ) : (
            <>Your answers have been securely saved. The hiring team will be in touch with the next steps.</>
          )}
        </div>

        <SecondaryButton onClick={() => (window.location.href = '/candidates/applications')}>
          Return to Dashboard
        </SecondaryButton>
      </Card>
    </FadeIn>
  );

  if (testStore.isLoading && !testStore.title) {
    return <PageContainer>Loading assessment...</PageContainer>;
  }

  return (
    <PageContainer $isActive={testState === 'active'}>
      {showWarning && (
        <WarningModalOverlay>
          <WarningCard>
            <h3>⚠️ Action Prohibited</h3>
            <p>Please stay focused on this tab. Leaving the window may result in test termination.</p>
            <PrimaryButton onClick={() => setShowWarning(false)}>I Understand</PrimaryButton>
          </WarningCard>
        </WarningModalOverlay>
      )}

      {testState === 'intro' && renderIntro()}
      {testState === 'active' && testStore.testType === 'fdc_test' && renderCustomTest()}
      {testState === 'active' && testStore.testType !== 'fdc_test' && renderActive()}
      {testState === 'completed' && renderCompleted()}

      {feedbackToDisplay && testStore.applicationId && (
        <React.Suspense fallback={null}>
          <RatingModal
            feedback={feedbackToDisplay}
            applicationId={testStore.applicationId}
            jobTitle={testStore.job?.position_title}
            companyName={testStore.company?.name}
            onClose={() => window.location.href = '/candidates/applications'}
            onComplete={() => window.location.href = '/candidates/applications'}
          />
        </React.Suspense>
      )}
    </PageContainer>
  );
});
